import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';
import { SortingState } from '@hub/libs/bearing-hub.helper';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import type { ParsedUrlQuery } from 'querystring';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import type { DimensionKey, IbhDimensions, ShapeDescriptor } from '@hub/bearing-hub/common/types';
import { ShapeDimensionsDefinition } from '@hub/bearing-hub/common/constants';
import { useMediaQuery } from '@hub/hooks/useMediaQuery';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';

export type Headers =
  | 'image'
  | 'material'
  | 'dimensions'
  | 'deliveryTime'
  | 'lifetime'
  | 'price'
  | 'productionMethod'
  | 'selection'
  | DimensionKey;

export interface BearingsTableInitProps {
  selectedShape: ShapeDescriptor;
}

export interface HeaderDefinition {
  key: Headers;
  sorting: boolean;
  sortingState: SortingState;
}

interface BearingsTableState {
  tableHeaders: HeaderDefinition[];
  selectedShape: ShapeDescriptor;
  shapeDimensionsHeadersKeysToDisplay: DimensionKey[];
  allShapeDimensionsHeadersKeys: DimensionKey[];
  presentDimensionsDataFrom: number;
  presentDimensionsDataTo: number;
  currentSortingHeaderKey: Headers;
  currentSortingHeaderState: SortingState;
  tableIsSortingByDefault: boolean;
  isTableFullScreen: boolean;
}

export interface BearingsTableStore extends BearingsTableState {
  updateChangeRangeDirection: (changeRangeDirection: RangeDirectionChange) => void;
  setTableHeadersSortingState: (headerKey: Headers, newSortingState?: SortingState) => void;
  setIsTableFullScreen: () => void;
}

export enum RangeDirectionChange {
  Forward = 'forward',
  Back = 'back',
}

const DefaultPresentDimensionsDataRange = {
  from: 0,
  to: 4,
};

const getDefaultPresentDimensionsDataRange = (isTableFullScreen: boolean, isBigScreen: boolean) => {
  let maxRange: number;
  if (isTableFullScreen) {
    maxRange = 8;
  } else if (isBigScreen) {
    maxRange = 5;
  } else {
    maxRange = DefaultPresentDimensionsDataRange.to;
  }

  return {
    from: 0,
    to: maxRange,
  };
};

const defaultHeaders: Headers[] = ['image', 'material', 'dimensions', 'deliveryTime', 'lifetime', 'price', 'selection'];
const headersNoSort: Headers[] = ['image', 'dimensions', 'selection'];

export const getNewSortingState = (sortingStateArg: SortingState) => {
  if (sortingStateArg === SortingState.Ascending) {
    return SortingState.Descending;
  }
  if (sortingStateArg === SortingState.NoSort) {
    return SortingState.Ascending;
  }
  return SortingState.NoSort;
};

const createHeaderDefinition = (
  shapeDimensionsHeadersKeysToDisplay: DimensionKey[],
  initKey: Headers,
  initSortingState: SortingState,
  hiddenHeaders: Headers[] = []
): HeaderDefinition[] => {
  const allHeadersDefinition = [...defaultHeaders, ...shapeDimensionsHeadersKeysToDisplay].map(key => ({
    key,
    sorting: !headersNoSort.includes(key),
    sortingState: SortingState.NoSort,
  }));

  return allHeadersDefinition
    .map(headerToDisplay => {
      if (headerToDisplay.key === initKey) {
        return { ...headerToDisplay, sortingState: initSortingState };
      }
      return { ...headerToDisplay, sortingState: SortingState.NoSort };
    })
    .filter(header => !hiddenHeaders.includes(header.key));
};

const getSortingParams = (
  sortingByQuery: string
): { sortingStateFromQuery: SortingState; headerKeyFromQuery: Headers } => {
  const sortingStateFromQuery = sortingByQuery?.split('-')[0] as SortingState;
  const headerKeyFromQuery = sortingByQuery?.split('-')[1] as Headers;
  return { sortingStateFromQuery, headerKeyFromQuery };
};

export const createTableStore = (
  initProps: BearingsTableInitProps,
  query: ParsedUrlQuery,
  dimensions: IbhDimensions,
  isBigScreen: boolean
) => {
  const DefaultSortingKey: Headers = 'price';
  const DefaultSortingState: SortingState = SortingState.Ascending;
  const isTableFullScreen = false;

  const { sortingStateFromQuery, headerKeyFromQuery } = getSortingParams(query.sortingBy as string);
  const allShapeDimensionsHeadersKeys = ShapeDimensionsDefinition[initProps.selectedShape.shapeKey];
  const defaultRange = getDefaultPresentDimensionsDataRange(isTableFullScreen, isBigScreen);
  const initShapeDimensionsHeadersKeysToDisplay = allShapeDimensionsHeadersKeys.slice(
    defaultRange.from,
    defaultRange.to
  );

  return createStore<BearingsTableStore>()(set => ({
    shapeDimensionsHeadersKeysToDisplay: initShapeDimensionsHeadersKeysToDisplay,
    presentDimensionsDataFrom: DefaultPresentDimensionsDataRange.from,
    presentDimensionsDataTo: DefaultPresentDimensionsDataRange.to,
    currentSortingHeaderKey: headerKeyFromQuery || DefaultSortingKey,
    currentSortingHeaderState: sortingStateFromQuery || DefaultSortingState,
    tableIsSortingByDefault: true,
    allShapeDimensionsHeadersKeys,
    isTableFullScreen,
    tableHeaders: createHeaderDefinition(
      initShapeDimensionsHeadersKeysToDisplay,
      headerKeyFromQuery || DefaultSortingKey,
      sortingStateFromQuery || DefaultSortingState
    ),
    ...initProps,

    updateChangeRangeDirection: changeRangeDirection =>
      set(state => {
        if (
          state.presentDimensionsDataTo < state.allShapeDimensionsHeadersKeys.length &&
          changeRangeDirection === 'forward'
        ) {
          return {
            shapeDimensionsHeadersKeysToDisplay: allShapeDimensionsHeadersKeys.slice(
              state.presentDimensionsDataFrom + 1,
              state.presentDimensionsDataTo + 1
            ),
            presentDimensionsDataFrom: state.presentDimensionsDataFrom + 1,
            presentDimensionsDataTo: state.presentDimensionsDataTo + 1,
            tableHeaders: createHeaderDefinition(
              allShapeDimensionsHeadersKeys.slice(
                state.presentDimensionsDataFrom + 1,
                state.presentDimensionsDataTo + 1
              ),
              state.currentSortingHeaderKey,
              state.currentSortingHeaderState
            ),
          };
        }

        if (state.presentDimensionsDataFrom > 0 && changeRangeDirection === 'back') {
          return {
            shapeDimensionsHeadersKeysToDisplay: allShapeDimensionsHeadersKeys.slice(
              state.presentDimensionsDataFrom - 1,
              state.presentDimensionsDataTo - 1
            ),
            presentDimensionsDataFrom: state.presentDimensionsDataFrom - 1,
            presentDimensionsDataTo: state.presentDimensionsDataTo - 1,
            tableHeaders: createHeaderDefinition(
              allShapeDimensionsHeadersKeys.slice(
                state.presentDimensionsDataFrom - 1,
                state.presentDimensionsDataTo - 1
              ),
              state.currentSortingHeaderKey,
              state.currentSortingHeaderState
            ),
          };
        }

        return state;
      }),

    setTableHeadersSortingState: (key, newSortingState) =>
      set(state => {
        const currentSortingHeader = state.tableHeaders.find(header => header.key === key);
        return {
          currentSortingHeaderKey: key,
          currentSortingHeaderState: newSortingState || getNewSortingState(currentSortingHeader.sortingState),
          tableIsSortingByDefault: false,
          tableHeaders: state.tableHeaders.map(header => ({
            ...header,
            sortingState:
              header.key === key ? newSortingState || getNewSortingState(header.sortingState) : SortingState.NoSort,
          })),
        };
      }),

    setIsTableFullScreen: () =>
      set(state => {
        const value = !state.isTableFullScreen;
        const newRange = getDefaultPresentDimensionsDataRange(value, isBigScreen);

        return {
          isTableFullScreen: value,
          presentDimensionsDataFrom: newRange.from,
          presentDimensionsDataTo: newRange.to,
          shapeDimensionsHeadersKeysToDisplay: state.allShapeDimensionsHeadersKeys.slice(newRange.from, newRange.to),
          tableHeaders: createHeaderDefinition(
            state.allShapeDimensionsHeadersKeys.slice(newRange.from, newRange.to),
            state.currentSortingHeaderKey,
            state.currentSortingHeaderState
          ),
        };
      }),
  }));
};
const StoreContext = createContext<StoreApi<BearingsTableStore>>(null);

type BearingTableStoreProviderProps = {
  initialData: BearingsTableInitProps;
  children?: ReactNode;
};

export const BearingsTableStoreProvider = ({ children, initialData }: BearingTableStoreProviderProps) => {
  const query = useNextHashQueryParams();
  const shape = initialData.selectedShape.shapeKey;
  const isBigScreen = useMediaQuery('(min-width: 1200px)');
  const { dimensions } = useDimensionsStore();
  const storeRef = useRef<StoreApi<BearingsTableStore>>();
  storeRef.current = createTableStore(initialData, query, dimensions[shape], isBigScreen);

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
};

export const useBearingsTableStore = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  return useStore(store);
};
